import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { API, getAuthHeader } from '@/App';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Calendar, Download, Printer, BarChart3, TrendingUp,
  Globe, FileText, ChevronDown, RefreshCw, Package,
  Link2, CalendarRange, CalendarDays, PieChart, GitBranch, Clock, Mail, Ban, Scale, Users, ShoppingCart, History, ClipboardList
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart as RechartsPie, Pie, Cell, ResponsiveContainer,
  AreaChart, Area, LineChart, Line,
} from 'recharts';

const CHART_COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316', '#ec4899'];

const TAB_CONFIG = [
  { id: 'client', label: 'Client Code', icon: BarChart3 },
  { id: 'country', label: 'Country', icon: Globe },
  { id: 'invoice', label: 'Invoice', icon: FileText },
  { id: 'invoiceFlow', label: 'Invoice Flow', icon: GitBranch },
  { id: 'charts', label: 'Charts', icon: PieChart },
  { id: 'cancellations', label: 'Cancellations', icon: Ban },
  { id: 'packers', label: 'Packers', icon: Scale },
  { id: 'processors', label: 'Processors', icon: Users, adminOnly: true },
  { id: 'demand', label: 'Product Demand', icon: ShoppingCart },
  { id: 'changes', label: 'Changes', icon: History, adminOnly: true },
  { id: 'timeline', label: 'Timeline', icon: Clock, adminOnly: true },
  { id: 'leaderboard', label: 'Leaderboard', icon: Users },
  { id: 'reasons', label: 'Reasons', icon: ClipboardList, roles: ['admin', 'supervisor', 'manager'] },
];

const RANGE_PRESETS = [
  { id: '7d', label: 'Last 7 Days', days: 7 },
  { id: '14d', label: 'Last 14 Days', days: 14 },
  { id: '30d', label: 'Last 30 Days', days: 30 },
  { id: 'month', label: 'This Month', days: 0 },
];

const formatDate = (dateStr) => {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const getDateNDaysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
};

const getMonthStart = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
};

import ManagementReports from './ManagementReports';
import { ProcessorReport } from './ProcessorReport';
import { ProductDemandReport } from './ProductDemandReport';
import { PackersView } from './PackerWeekly';
import { ChangesReview } from './ChangesReview';
import { WorkTimeline } from './WorkTimeline';
import { Leaderboard } from './Leaderboard';
import { ReasonReport } from './ReasonReport';
import { ParcelSummaryButton } from './ParcelSummaryButton';
import { DocPrintBar, SelectBox, useDocSelection } from './DocPrintBar';
import { useInvoiceFilters, InvoiceFilterBar, filterInvoices, isFiltered } from './InvoiceFilters';
import { rowTurnaround, fmtHours, useTurnaroundLimits, TurnaroundLimitsEditor, TurnaroundSummary, TurnaroundDigestButton, stepLimitHours } from './InvoiceTurnaround';

const ReportsPanel = ({ user }) => {
  const [view, setView] = useState('dispatch'); // 'dispatch' | 'management'

  return (
    <div className="flex flex-col h-[calc(100vh-140px)] max-md:h-auto" data-testid="reports-panel">
      {/* View Toggle */}
      <div className="flex gap-1 mb-3 bg-white rounded-lg p-1 w-fit shadow-sm border border-slate-200">
        <button
          onClick={() => setView('dispatch')}
          data-testid="view-dispatch"
          className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-all ${
            view === 'dispatch' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
          }`}
        >
          <BarChart3 className="w-4 h-4" />Dispatch Reports
        </button>
        <button
          onClick={() => setView('management')}
          data-testid="view-management"
          className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-all ${
            view === 'management' ? 'bg-teal-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
          }`}
        >
          <TrendingUp className="w-4 h-4" />Management Reports
        </button>
      </div>
      {view === 'dispatch' ? <DispatchReports user={user} /> : <ManagementReports />}
    </div>
  );
};

const DispatchReports = ({ user }) => {
  const today = new Date().toISOString().split('T')[0];
  const [mode, setMode] = useState('single'); // 'single' | 'range'
  const [selectedDate, setSelectedDate] = useState(today);
  const [dateFrom, setDateFrom] = useState(getDateNDaysAgo(7));
  const [dateTo, setDateTo] = useState(today);
  const [availableDates, setAvailableDates] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [invoiceFlowData, setInvoiceFlowData] = useState(null);
  const [cancellationsData, setCancellationsData] = useState(null);
  const [packersData, setPackersData] = useState(null);
  const [orCancelledStats, setOrCancelledStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('client');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [invFilters, setInvFilters, resetInvFilters] = useInvoiceFilters(user);
  const printRef = useRef(null);

  useEffect(() => {
    const fetchDates = async () => {
      try {
        const res = await axios.get(`${API}/reports/dates`, { headers: getAuthHeader() });
        setAvailableDates(res.data);
      } catch { /* ignore */ }
    };
    fetchDates();
    // Iter 265 — Order Register cancellation accountability counters.
    axios.get(`${API}/order-register/cancelled-stats`,
      { headers: getAuthHeader() })
      .then(r => setOrCancelledStats(r.data))
      .catch(() => setOrCancelledStats(null));
  }, []);

  const generateReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = mode === 'single'
        ? `date=${selectedDate}`
        : `date_from=${dateFrom}&date_to=${dateTo}`;
      const [reportRes, flowRes, cancelRes, packersRes] = await Promise.all([
        axios.get(`${API}/reports/generate?${params}`, { headers: getAuthHeader() }),
        axios.get(`${API}/reports/invoice-flow?${params}`, { headers: getAuthHeader() }).catch(() => ({ data: { invoice_flow: [], total_invoices: 0 } })),
        axios.get(`${API}/reports/cancellations?${params}`, { headers: getAuthHeader() }).catch(() => ({ data: { cancellations: [], total: 0 } })),
        axios.get(`${API}/reports/packers?${params}`, { headers: getAuthHeader() }).catch(() => ({ data: { rows: [], total: 0 } })),
      ]);
      setReportData(reportRes.data);
      setInvoiceFlowData(flowRes.data);
      setCancellationsData(cancelRes.data);
      setPackersData(packersRes.data);
      if (reportRes.data.total_parcels === 0 && flowRes.data.total_invoices === 0) {
        toast.info('No data found for selected period');
      } else {
        toast.success(`Report loaded: ${reportRes.data.total_parcels} parcels, ${flowRes.data.total_invoices} invoice flows`);
      }
    } catch {
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  }, [mode, selectedDate, dateFrom, dateTo]);

  const downloadExcel = useCallback(async () => {
    try {
      const params = mode === 'single'
        ? `date=${selectedDate}`
        : `date_from=${dateFrom}&date_to=${dateTo}`;
      const fq = `&inv_countries=${encodeURIComponent(invFilters.countries.join(','))}&inv_services=${encodeURIComponent(invFilters.services.join(','))}`;
      const res = await axios.get(`${API}/reports/excel?${params}${fq}`, {
        headers: getAuthHeader(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Dispatch_Report.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Excel downloaded');
    } catch {
      toast.error('Failed to download');
    }
  }, [mode, selectedDate, dateFrom, dateTo, invFilters]);

  const handlePrint = useCallback(() => {
    if (!printRef.current) return;
    const printContent = printRef.current.innerHTML;
    const win = window.open('', '_blank');
    win.document.write(`
      <html><head><title>Dispatch Report</title>
      <style>
        body { font-family: Calibri, sans-serif; padding: 20px; color: #1e293b; }
        table { border-collapse: collapse; width: 100%; margin-bottom: 24px; }
        th, td { border: 1px solid #cbd5e1; padding: 6px 10px; text-align: center; font-size: 12px; }
        th { background: #2F5496; color: white; font-weight: 600; }
        .total-row td { background: #D6E4F0; font-weight: 700; }
        h2 { color: #2F5496; margin: 16px 0 8px; font-size: 16px; }
        .report-title { font-size: 20px; color: #2F5496; margin-bottom: 16px; }
        td:first-child { text-align: left; }
        .no-print { display: none !important; }
      </style></head><body>${printContent}</body></html>
    `);
    win.document.close();
    win.print();
  }, []);

  const copyLink = useCallback(() => {
    const params = mode === 'single' ? `date=${selectedDate}` : `from=${dateFrom}&to=${dateTo}`;
    const url = `${window.location.origin}${window.location.pathname}?panel=reports&${params}`;
    navigator.clipboard.writeText(url).then(() => toast.success('Link copied'));
  }, [mode, selectedDate, dateFrom, dateTo]);

  const applyPreset = (preset) => {
    if (preset.id === 'month') {
      setDateFrom(getMonthStart());
    } else {
      setDateFrom(getDateNDaysAgo(preset.days));
    }
    setDateTo(today);
    setShowDatePicker(false);
  };

  const dateLabel = useMemo(() => {
    if (mode === 'single') return formatDate(selectedDate);
    return `${formatDate(dateFrom)} — ${formatDate(dateTo)}`;
  }, [mode, selectedDate, dateFrom, dateTo]);

  return (
    <div className="flex flex-col flex-1 overflow-hidden max-md:overflow-visible" data-testid="dispatch-reports">
      {/* Top Bar */}
      <div className="flex items-center justify-between gap-3 bg-white rounded-xl shadow-sm border border-slate-200 px-4 py-3 mb-3 flex-wrap">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-5 h-5 text-blue-600" />
          <h2 className="text-base font-semibold text-slate-900">Dispatch Reports</h2>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Mode Toggle */}
          <div className="flex bg-slate-100 rounded-lg p-0.5">
            <button
              onClick={() => setMode('single')}
              data-testid="mode-single"
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all ${
                mode === 'single' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <CalendarDays className="w-3.5 h-3.5" />
              Day
            </button>
            <button
              onClick={() => setMode('range')}
              data-testid="mode-range"
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all ${
                mode === 'range' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <CalendarRange className="w-3.5 h-3.5" />
              Range
            </button>
          </div>

          {/* Date Selector */}
          <div className="relative">
            <button
              onClick={() => setShowDatePicker(!showDatePicker)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700 transition-colors max-w-[280px]"
              data-testid="date-picker-btn"
            >
              <Calendar className="w-4 h-4 text-blue-500 flex-shrink-0" />
              <span className="truncate">{dateLabel}</span>
              <ChevronDown className="w-3 h-3 flex-shrink-0" />
            </button>
            {showDatePicker && (
              <div className="absolute top-full right-0 mt-1 bg-white rounded-lg shadow-xl border border-slate-200 p-3 z-30 min-w-[260px] max-sm:left-0 max-sm:right-auto max-sm:max-w-[calc(100vw-3rem)]">
                {mode === 'single' ? (
                  <>
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => { setSelectedDate(e.target.value); setShowDatePicker(false); }}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-blue-400"
                      data-testid="date-input"
                    />
                    {availableDates.length > 0 && (
                      <>
                        <div className="text-xs text-slate-400 mt-2 mb-1 px-1">Dates with data:</div>
                        <div className="max-h-[180px] overflow-y-auto">
                          {availableDates.slice(0, 15).map(d => (
                            <button
                              key={d}
                              onClick={() => { setSelectedDate(d); setShowDatePicker(false); }}
                              className={`w-full text-left px-2 py-1.5 text-sm rounded hover:bg-blue-50 transition-colors ${
                                d === selectedDate ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-slate-600'
                              }`}
                            >
                              {formatDate(d)}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {/* Quick presets */}
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {RANGE_PRESETS.map(p => (
                        <button
                          key={p.id}
                          onClick={() => applyPreset(p)}
                          data-testid={`preset-${p.id}`}
                          className="px-2.5 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                    <div className="space-y-2">
                      <label className="block text-xs text-slate-500 font-medium">From</label>
                      <input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-blue-400"
                        data-testid="date-from-input"
                      />
                      <label className="block text-xs text-slate-500 font-medium">To</label>
                      <input
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-blue-400"
                        data-testid="date-to-input"
                      />
                      <Button
                        size="sm"
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white mt-1"
                        onClick={() => setShowDatePicker(false)}
                      >
                        Apply Range
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <Button
            onClick={generateReport}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            size="sm"
            data-testid="generate-report-btn"
          >
            {loading ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <BarChart3 className="w-4 h-4 mr-1" />}
            {loading ? 'Loading...' : 'Generate'}
          </Button>

          {reportData && reportData.total_parcels > 0 && (
            <>
              <Button variant="outline" size="sm" onClick={downloadExcel} data-testid="download-excel-btn">
                <Download className="w-4 h-4 mr-1" />
                Excel
              </Button>
              <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-btn">
                <Printer className="w-4 h-4 mr-1" />
                Print
              </Button>
              <Button variant="outline" size="sm" onClick={copyLink} data-testid="copy-link-btn">
                <Link2 className="w-4 h-4 mr-1" />
                Share
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Content */}
      {!reportData ? (
        <><EmptyState onGenerate={generateReport} loading={loading} /><StandaloneReports user={user} /></>
      ) : reportData.total_parcels === 0
          && (reportData.total_cancelled || 0) === 0
          && (cancellationsData?.total || 0) === 0 ? (
        <><NoDataState label={dateLabel} /><StandaloneReports user={user} /></>
      ) : (
        <div className="flex-1 overflow-hidden flex flex-col max-md:overflow-visible max-md:flex-none">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-3">
            <SummaryCard
              label="Total Parcels"
              value={reportData.total_parcels}
              icon={Package}
              color="blue"
              extra={reportData.total_cancelled > 0 && (
                <span
                  className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-pink-100 text-pink-800 text-[10px] font-semibold"
                  title="Cancelled parcels — already excluded from this total"
                  data-testid="summary-cancelled-badge"
                >
                  +{reportData.total_cancelled} cancelled
                </span>
              )}
            />
            <SummaryCard label="Client Codes" value={reportData.client_code_data.length} icon={BarChart3} color="emerald" />
            <SummaryCard label="Countries" value={reportData.country_data.length} icon={Globe} color="amber" />
            <SummaryCard label="Invoices" value={reportData.invoice_data.length} icon={FileText} color="violet" />
            <SummaryCard
              label={orCancelledStats ? `Cancelled · ${orCancelledStats.month_label}` : "Cancelled this month"}
              value={orCancelledStats?.this_month ?? "—"}
              icon={Ban}
              color="rose"
              dataTestid="summary-or-cancelled-month"
              extra={orCancelledStats && (
                <span
                  className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-800 text-[10px] font-semibold"
                  title={`Today: ${orCancelledStats.today} · Total ever: ${orCancelledStats.total}`}
                  data-testid="summary-or-cancelled-today"
                >
                  today {orCancelledStats.today}
                </span>
              )}
            />
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-3 bg-slate-100 rounded-lg p-1 w-fit max-md:w-full max-md:overflow-x-auto [&>button]:shrink-0">
            {TAB_CONFIG.filter(t => (t.roles ? t.roles.includes(user?.role) : (!t.adminOnly || user?.role === 'admin'))).map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  activeTab === tab.id ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-auto bg-white rounded-xl shadow-sm border border-slate-200 max-md:overflow-visible max-md:flex-none" ref={printRef}>
            {activeTab !== 'charts' && (
              <div className="report-title p-4 pb-0 text-lg font-semibold text-[#2F5496]">
                {activeTab === 'client' && `Client Code Report — ${dateLabel}`}
                {activeTab === 'country' && `Country Wise Parcels — ${dateLabel}`}
                {activeTab === 'invoice' && <span className="flex flex-wrap items-center justify-between gap-2">{`Invoice Wise Parcels — ${dateLabel}${isFiltered(invFilters) ? ` (${[invFilters.countries.join(', '), invFilters.services.join(', ')].filter(Boolean).join(' · ')})` : ''}`}
                  <ParcelSummaryButton params={mode === 'range' ? { date_from: dateFrom, date_to: dateTo } : { date: selectedDate }} /></span>}
                {activeTab === 'invoiceFlow' && `Invoice Processing Flow — ${dateLabel}`}
                {activeTab === 'cancellations' && `Cancelled Parcels — ${dateLabel}`}
                {activeTab === 'packers' && `Packer Output — ${dateLabel}`}
                {activeTab === 'processors' && `Processor Output — ${dateLabel}`}
                {activeTab === 'demand' && 'Product Demand — Top Products by Quantity'}
                {activeTab === 'changes' && 'Changes Review — Invoice / Tracking / Service edits per team member'}
                {activeTab === 'timeline' && 'Daily Work Timeline — parcels started per member'}
                {activeTab === 'leaderboard' && 'Leaderboard'}
                {activeTab === 'reasons' && 'Reason Report — monthly deletes / edits / cancels by person × reason'}
              </div>
            )}
            {activeTab === 'client' && <ClientCodeTable data={reportData} />}
            {activeTab === 'country' && <CountryTable data={reportData} />}
            {activeTab === 'invoice' && <InvoiceTable data={reportData} filters={invFilters} setFilters={setInvFilters} reset={resetInvFilters} />}
            {activeTab === 'invoiceFlow' && <InvoiceFlowTable data={invoiceFlowData} user={user} />}
            {activeTab === 'changes' && <ChangesReview />}
            {activeTab === 'timeline' && <WorkTimeline />}
            {activeTab === 'leaderboard' && <Leaderboard />}
            {activeTab === 'reasons' && <ReasonReport />}
            {activeTab === 'demand' && <ProductDemandReport isAdmin={user?.role === 'admin'} />}
            {activeTab === 'charts' && <ChartsView data={reportData} dateLabel={dateLabel} isRange={mode === 'range'} />}
            {activeTab === 'cancellations' && (
              <CancellationsTable
                data={cancellationsData}
                dateParams={mode === 'range' ? { date_from: dateFrom, date_to: dateTo } : { date: selectedDate }}
              />
            )}
            {activeTab === 'packers' && <PackersView daily={<PackersTable data={packersData} />} isAdmin={user?.role === 'admin'} />}
            {activeTab === 'processors' && (
              <ProcessorReport dateFrom={mode === 'single' ? selectedDate : dateFrom} dateTo={mode === 'single' ? selectedDate : dateTo} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Sub-components ─── */

const SummaryCard = ({ label, value, icon: Icon, color, extra, dataTestid }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    violet: 'bg-violet-50 text-violet-700 border-violet-200',
    rose: 'bg-rose-50 text-rose-700 border-rose-200',
  };
  return (
    <div className={`rounded-xl border px-4 py-3 ${colors[color]}`} data-testid={dataTestid || `summary-${label.toLowerCase().replace(/ /g, '-')}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4 opacity-70" />
        <span className="text-xs font-medium opacity-80">{label}</span>
      </div>
      <div className="flex items-baseline gap-2 flex-wrap">
        <div className="text-2xl font-bold">{value}</div>
        {extra}
      </div>
    </div>
  );
};

const EmptyState = ({ onGenerate, loading }) => (
  <div className="flex-1 flex items-center justify-center bg-white rounded-xl shadow-sm border border-slate-200">
    <div className="text-center">
      <BarChart3 className="w-14 h-14 mx-auto mb-4 text-slate-300" />
      <h3 className="text-lg font-semibold text-slate-700 mb-1">Daily Dispatch Reports</h3>
      <p className="text-sm text-slate-400 mb-4">Select a date or range and generate a report</p>
      <Button onClick={onGenerate} disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="generate-report-empty-btn">
        <BarChart3 className="w-4 h-4 mr-1.5" />
        Generate Report
      </Button>
    </div>
  </div>
);

// Reports that don't depend on dispatch data — reachable even before / without a generated report.
const StandaloneReports = ({ user }) => {
  const isAdmin = user?.role === 'admin';
  const [tab, setTab] = useState('');
  const opts = [...(isAdmin ? [['changes', 'Changes Review'], ['timeline', 'Work Timeline']] : []), ['leaderboard', 'Leaderboard'], ['demand', 'Product Demand'],
    ...(['admin', 'supervisor', 'manager'].includes(user?.role) ? [['reasons', 'Reason Report']] : [])];
  return (
    <div className="mt-3 bg-white rounded-xl border border-slate-200" data-testid="standalone-reports">
      <div className="flex flex-wrap items-center gap-2 px-4 py-2 border-b border-slate-100">
        <span className="text-xs font-semibold text-slate-500">Also available:</span>
        {opts.map(([id, label]) => (
          <button key={id} onClick={() => setTab(t => t === id ? '' : id)} data-testid={`standalone-${id}`}
            className={`px-3 py-1 rounded-full text-xs font-semibold border ${tab === id ? 'bg-[#2F5496] text-white border-[#2F5496]' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}>{label}</button>
        ))}
      </div>
      {tab === 'changes' && <ChangesReview />}
      {tab === 'timeline' && <WorkTimeline />}
      {tab === 'leaderboard' && <Leaderboard />}
      {tab === 'demand' && <ProductDemandReport isAdmin={isAdmin} />}
      {tab === 'reasons' && <ReasonReport />}
    </div>
  );
};

const NoDataState = ({ label }) => (
  <div className="flex-1 flex items-center justify-center bg-white rounded-xl shadow-sm border border-slate-200">
    <div className="text-center">
      <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-300" />
      <h3 className="text-base font-semibold text-slate-600">No data for {label}</h3>
      <p className="text-sm text-slate-400 mt-1">No packing lists were processed in this period</p>
    </div>
  </div>
);

/* ── Charts View ── */
const ChartsView = ({ data, dateLabel, isRange }) => {
  const { services, client_code_data, country_data, daily_trend } = data;

  // Service distribution pie data
  const servicePieData = useMemo(() => {
    return services.map(svc => ({
      name: svc,
      value: client_code_data.reduce((sum, r) => sum + (r[svc] || 0), 0),
    })).filter(d => d.value > 0);
  }, [services, client_code_data]);

  // Client code bar data (top 10)
  const clientBarData = useMemo(() => {
    return [...client_code_data]
      .sort((a, b) => b.grand_total - a.grand_total)
      .slice(0, 10);
  }, [client_code_data]);

  // Country pie data (top 8)
  const countryPieData = useMemo(() => {
    const top = country_data.slice(0, 8);
    const rest = country_data.slice(8).reduce((sum, r) => sum + r.parcels, 0);
    const result = top.map(r => ({ name: r.country, value: r.parcels }));
    if (rest > 0) result.push({ name: 'Others', value: rest });
    return result;
  }, [country_data]);

  // Trend data for area chart
  const trendData = useMemo(() => {
    return (daily_trend || []).map(d => ({
      ...d,
      date: formatDate(d.date),
    }));
  }, [daily_trend]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload) return null;
    return (
      <div className="bg-white rounded-lg shadow-lg border border-slate-200 px-3 py-2 text-sm">
        <div className="font-semibold text-slate-800 mb-1">{label}</div>
        {payload.map((p, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full" style={{ background: p.color }} />
            <span className="text-slate-600">{p.name}:</span>
            <span className="font-semibold">{p.value}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="p-4 space-y-6" data-testid="charts-view">
      <h3 className="text-lg font-semibold text-[#2F5496]">Visual Analytics — {dateLabel}</h3>

      {/* Row 1: Service Distribution + Country Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Service Distribution */}
        <div className="border border-slate-200 rounded-xl p-4">
          <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <PieChart className="w-4 h-4 text-blue-500" />
            Service Distribution
          </h4>
          <ResponsiveContainer width="100%" height={240}>
            <RechartsPie>
              <Pie
                data={servicePieData}
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={90}
                paddingAngle={3}
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {servicePieData.map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </RechartsPie>
          </ResponsiveContainer>
        </div>

        {/* Country Distribution */}
        <div className="border border-slate-200 rounded-xl p-4">
          <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <Globe className="w-4 h-4 text-amber-500" />
            Country Distribution
          </h4>
          <ResponsiveContainer width="100%" height={240}>
            <RechartsPie>
              <Pie
                data={countryPieData}
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={90}
                paddingAngle={3}
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {countryPieData.map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[(i + 2) % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </RechartsPie>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Row 2: Client Code Bar Chart */}
      <div className="border border-slate-200 rounded-xl p-4">
        <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-emerald-500" />
          Top Client Codes by Parcels
        </h4>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={clientBarData} margin={{ left: 0, right: 16, top: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="code" tick={{ fontSize: 11, fill: '#64748b' }} />
            <YAxis tick={{ fontSize: 11, fill: '#64748b' }} allowDecimals={false} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {services.map((svc, i) => (
              <Bar key={svc} dataKey={svc} stackId="a" fill={CHART_COLORS[i % CHART_COLORS.length]} radius={i === services.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Row 3: Daily Trend (only for range mode or if multiple days) */}
      {trendData.length > 1 && (
        <div className="border border-slate-200 rounded-xl p-4">
          <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-violet-500" />
            Daily Dispatch Trend
          </h4>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={trendData} margin={{ left: 0, right: 16, top: 8, bottom: 8 }}>
              <defs>
                {services.map((svc, i) => (
                  <linearGradient key={svc} id={`grad-${svc}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_COLORS[i % CHART_COLORS.length]} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={CHART_COLORS[i % CHART_COLORS.length]} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#64748b' }} />
              <YAxis tick={{ fontSize: 11, fill: '#64748b' }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {services.map((svc, i) => (
                <Area
                  key={svc}
                  type="monotone"
                  dataKey={svc}
                  stroke={CHART_COLORS[i % CHART_COLORS.length]}
                  fill={`url(#grad-${svc})`}
                  strokeWidth={2}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

/* ── Client Code Table ── */
const ClientCodeTable = ({ data }) => {
  const { services, client_code_data, client_code_total } = data;
  return (
    <div className="p-4 pt-2">
      <table className="w-full border-collapse" data-testid="client-code-table">
        <thead>
          <tr>
            <th className="bg-[#2F5496] text-white text-sm font-semibold py-2 px-3 text-left border border-slate-300">CODE</th>
            <th className="bg-[#2F5496] text-white text-sm font-semibold py-2 px-3 text-center border border-slate-300">Grand Total</th>
            {services.map(s => (
              <th key={s} className="bg-[#2F5496] text-white text-sm font-semibold py-2 px-3 text-center border border-slate-300">{s}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {client_code_data.map((row, i) => (
            <tr key={i} className="hover:bg-slate-50 transition-colors">
              <td className="py-1.5 px-3 text-sm font-semibold text-slate-800 border border-slate-200">{row.code}</td>
              <td className="py-1.5 px-3 text-sm text-center font-bold text-slate-900 border border-slate-200">
                {row.grand_total}
                {row.cancelled > 0 && (
                  <span
                    className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full bg-pink-100 text-pink-800 text-[10px] font-semibold align-middle"
                    title={`${row.cancelled} parcel${row.cancelled === 1 ? '' : 's'} cancelled from ${row.code}`}
                    data-testid={`client-cancelled-badge-${row.code}`}
                  >
                    +{row.cancelled} cancelled
                  </span>
                )}
              </td>
              {services.map(s => (
                <td key={s} className="py-1.5 px-3 text-sm text-center text-slate-600 border border-slate-200">
                  {row[s] || ''}
                </td>
              ))}
            </tr>
          ))}
          <tr className="total-row bg-[#D6E4F0]">
            <td className="py-2 px-3 text-sm font-bold text-slate-900 border border-slate-300">{client_code_total.code}</td>
            <td className="py-2 px-3 text-sm text-center font-bold text-slate-900 border border-slate-300">{client_code_total.grand_total}</td>
            {services.map(s => (
              <td key={s} className="py-2 px-3 text-sm text-center font-bold text-slate-900 border border-slate-300">
                {client_code_total[s] || ''}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

/* ── Country Table ── */
const CountryTable = ({ data }) => {
  const { country_data, country_total } = data;
  return (
    <div className="p-4 pt-2">
      <table className="w-full border-collapse max-w-lg" data-testid="country-table">
        <thead>
          <tr>
            <th className="bg-[#2F5496] text-white text-sm font-semibold py-2 px-3 text-left border border-slate-300">Country</th>
            <th className="bg-[#2F5496] text-white text-sm font-semibold py-2 px-3 text-center border border-slate-300">Parcels</th>
          </tr>
        </thead>
        <tbody>
          {country_data.map((row, i) => (
            <tr key={i} className="hover:bg-slate-50 transition-colors">
              <td className="py-1.5 px-3 text-sm text-slate-800 border border-slate-200">{row.country}</td>
              <td className="py-1.5 px-3 text-sm text-center font-semibold text-slate-700 border border-slate-200">
                {row.parcels}
                {row.cancelled > 0 && (
                  <span
                    className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full bg-pink-100 text-pink-800 text-[10px] font-semibold align-middle"
                    title={`${row.cancelled} parcel${row.cancelled === 1 ? '' : 's'} cancelled going to ${row.country}`}
                    data-testid={`country-cancelled-badge-${row.country}`}
                  >
                    +{row.cancelled} cancelled
                  </span>
                )}
              </td>
            </tr>
          ))}
          <tr className="total-row bg-[#D6E4F0]">
            <td className="py-2 px-3 text-sm font-bold text-slate-900 border border-slate-300">Grand Total</td>
            <td className="py-2 px-3 text-sm text-center font-bold text-slate-900 border border-slate-300">{country_total}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

/* ── Invoice Table ── */
const InvoiceTable = ({ data, filters, setFilters, reset }) => {
  const { services: allSvcs, invoice_data } = data;
  const splitCountries = (s) => String(s || '')
    .split(',').map(x => x.trim()).filter(Boolean);
  const rowCountries = (r) => r.breakdown ? [...new Set(r.breakdown.map(b => b.country))].sort() : splitCountries(r.countries);
  const filtered = filterInvoices(data, filters);
  const services = filters.services.length ? allSvcs.filter(s => filters.services.includes(s)) : allSvcs;
  const totalShown = filtered.reduce((n, r) => n + (r.total || 0), 0);
  const toggleCountry = (c) => setFilters(f => ({ ...f, countries: f.countries.includes(c) ? f.countries.filter(x => x !== c) : [...f.countries, c] }));

  return (
    <div className="p-4 pt-2">
      <InvoiceFilterBar data={data} filters={filters} setFilters={setFilters} reset={reset}
                        shown={filtered.length} total={invoice_data.length} />
      <table className="w-full border-collapse" data-testid="invoice-table">
        <thead>
          <tr>
            <th className="bg-[#2F5496] text-white text-sm font-semibold py-2 px-3 text-left border border-slate-300">Invoice No</th>
            <th className="bg-[#2F5496] text-white text-sm font-semibold py-2 px-3 text-left border border-slate-300">Date</th>
            <th className="bg-[#2F5496] text-white text-sm font-semibold py-2 px-3 text-left border border-slate-300">Countries</th>
            <th className="bg-[#2F5496] text-white text-sm font-semibold py-2 px-3 text-center border border-slate-300">Total</th>
            <th className="bg-[#2F5496] text-white text-sm font-semibold py-2 px-3 text-center border border-slate-300">Weight</th>
            {services.map(s => (
              <th key={s} className="bg-[#2F5496] text-white text-sm font-semibold py-2 px-3 text-center border border-slate-300">{s}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filtered.map((row, i) => (
            <tr key={i} className="hover:bg-slate-50 transition-colors">
              <td className="py-1.5 px-3 text-sm font-medium text-slate-800 border border-slate-200">{row.invoice_no}</td>
              <td className="py-1.5 px-3 text-sm text-slate-600 border border-slate-200">{row.invoice_date}</td>
              <td className="py-1.5 px-3 text-sm border border-slate-200" data-testid={`invoice-countries-${row.invoice_no}`}>
                {rowCountries(row).length === 0 ? (
                  <span className="text-slate-400">—</span>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {rowCountries(row).map(c => {
                      const active = filters.countries.includes(c);
                      return (
                        <button
                          key={c}
                          type="button"
                          onClick={() => toggleCountry(c)}
                          data-testid={`invoice-country-chip-${row.invoice_no}-${c.replace(/\s+/g,'-')}`}
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border transition-colors ${
                            active
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-slate-100 text-slate-700 border-slate-200 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700'
                          }`}
                          title={active ? 'Click to clear filter' : `Filter invoices containing ${c}`}
                        >
                          {c}
                        </button>
                      );
                    })}
                  </div>
                )}
              </td>
              <td className="py-1.5 px-3 text-sm text-center font-bold text-slate-900 border border-slate-200">
                {row.total}
                {row.cancelled > 0 && (
                  <span
                    className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full bg-pink-100 text-pink-800 text-[10px] font-semibold align-middle"
                    title={`${row.cancelled} parcel${row.cancelled === 1 ? '' : 's'} cancelled from this invoice`}
                    data-testid={`invoice-cancelled-badge-${row.invoice_no}`}
                  >
                    +{row.cancelled} cancelled
                  </span>
                )}
              </td>
              <td className="py-1.5 px-3 text-sm text-center text-slate-700 border border-slate-200" data-testid={`invoice-weight-${row.invoice_no}`}
                  title={row.weighed_parcels != null ? `${row.weighed_parcels} of ${row.total} parcel(s) weighed` : ''}>
                {row.weight ? row.weight.toLocaleString('en-IN') : ''}
                {row.weight > 0 && row.weighed_parcels < row.total && <span className="ml-1 text-[10px] text-amber-600">({row.weighed_parcels}/{row.total})</span>}
              </td>
              {services.map(s => (
                <td key={s} className="py-1.5 px-3 text-sm text-center text-slate-600 border border-slate-200">
                  {row[s] || ''}
                </td>
              ))}
            </tr>
          ))}
          <tr className="total-row bg-[#D6E4F0]">
            <td className="py-2 px-3 text-sm font-bold text-slate-900 border border-slate-300">Grand Total</td>
            <td className="py-2 px-3 border border-slate-300"></td>
            <td className="py-2 px-3 border border-slate-300"></td>
            <td className="py-2 px-3 text-sm text-center font-bold text-slate-900 border border-slate-300" data-testid="invoice-grand-total">{totalShown}</td>
            <td className="py-2 px-3 text-sm text-center font-bold text-slate-900 border border-slate-300" data-testid="invoice-weight-total">
              {Math.round(filtered.reduce((n, r) => n + (r.weight || 0), 0) * 100) / 100 || ''}
            </td>
            {services.map(s => (
              <td key={s} className="py-2 px-3 text-sm text-center font-bold text-slate-900 border border-slate-300">
                {filtered.reduce((sum, r) => sum + (r[s] || 0), 0) || ''}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

/* ── Invoice Flow Table ── */
// Iter 148 — Packer output table (initials from Scan-Back Weights)
const PackersTable = ({ data }) => {
  if (!data) return null;
  const rows = data.rows || [];
  if (rows.length === 0) {
    return (
      <div className="p-8 text-center text-slate-500" data-testid="packers-empty">
        <Scale className="w-10 h-10 mx-auto mb-2 text-slate-300" />
        <div className="font-medium">No packer output recorded for this period</div>
        <div className="text-xs text-slate-400 mt-1">
          Scan a filled weight sheet (Packing List → Scan icon) and the packer initials will appear here.
        </div>
      </div>
    );
  }
  const totP = rows.reduce((s, r) => s + r.parcels, 0);
  const totG = rows.reduce((s, r) => s + r.total_grams, 0);
  return (
    <div className="p-4" data-testid="packers-table">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-slate-100 text-slate-700 text-xs uppercase tracking-wide">
            <th className="p-2 text-left border border-slate-200">Date</th>
            <th className="p-2 text-left border border-slate-200">Packer</th>
            <th className="p-2 text-center border border-slate-200">Parcels</th>
            <th className="p-2 text-right border border-slate-200">Total Weight (g)</th>
            <th className="p-2 text-right border border-slate-200">Avg / Parcel (g)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.date}-${r.packer}`} data-testid={`packer-row-${i}`}
                className={i % 2 ? 'bg-slate-50' : ''}>
              <td className="p-2 border border-slate-200">{r.date}</td>
              <td className="p-2 border border-slate-200 font-bold text-violet-700"
                  data-testid={`packer-name-${i}`}>{r.packer}</td>
              <td className="p-2 border border-slate-200 text-center"
                  data-testid={`packer-parcels-${i}`}>{r.parcels}</td>
              <td className="p-2 border border-slate-200 text-right font-medium"
                  data-testid={`packer-grams-${i}`}>{r.total_grams.toLocaleString()}</td>
              <td className="p-2 border border-slate-200 text-right text-slate-500">
                {r.parcels ? Math.round(r.total_grams / r.parcels) : 0}
              </td>
            </tr>
          ))}
          <tr className="bg-slate-800 text-white font-semibold" data-testid="packers-total-row">
            <td className="p-2 border border-slate-700" colSpan={2}>Grand Total</td>
            <td className="p-2 border border-slate-700 text-center">{totP}</td>
            <td className="p-2 border border-slate-700 text-right">{totG.toLocaleString()}</td>
            <td className="p-2 border border-slate-700 text-right">{totP ? Math.round(totG / totP) : 0}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const CancellationsTable = ({ data, dateParams }) => {
  const [trend, setTrend] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/reports/cancellations/trend?days=30`, {
          headers: getAuthHeader(),
        });
        if (alive) setTrend(res.data?.trend || []);
      } catch { /* silent — chart just won't render */ }
    })();
    return () => { alive = false; };
  }, []);

  if (!data) return null;
  const rows = data.cancellations || [];

  const downloadExcel = async () => {
    try {
      const params = new URLSearchParams(dateParams || {}).toString();
      const res = await axios.get(`${API}/reports/cancellations/excel?${params}`, {
        headers: getAuthHeader(),
        responseType: 'blob',
      });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Filename from Content-Disposition if present
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : 'cancellations.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Excel export failed');
    }
  };

  if (rows.length === 0) {
    return (
      <div className="p-8 text-center text-slate-500">
        <Ban className="w-10 h-10 mx-auto mb-2 text-slate-300" />
        <div className="font-medium">No cancellations for this period</div>
        <div className="text-xs text-slate-400 mt-1">Everything went out the door 🚀</div>
      </div>
    );
  }
  const fmtDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso;
      return d.toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    } catch { return iso; }
  };
  return (
    <div className="p-4">
      {/* Trend chart — last 30 days of cancellations across the whole shop */}
      {trend && trend.length > 0 && (
        <div className="mb-4 rounded-xl border border-pink-200 bg-gradient-to-br from-pink-50/60 to-white p-3">
          <div className="flex items-center justify-between mb-1">
            <div className="text-xs font-semibold text-pink-900 uppercase tracking-wide">
              Cancellations — last 30 days
            </div>
            <div className="text-[11px] text-slate-500">
              Total: <span className="font-semibold text-pink-800">
                {trend.reduce((s, t) => s + t.count, 0)}
              </span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={110}>
            <LineChart
              data={trend.map(t => ({
                ...t,
                short: (() => { try { return new Date(t.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); } catch { return t.date; } })(),
              }))}
              margin={{ top: 6, right: 8, left: -20, bottom: 0 }}
            >
              <defs>
                <linearGradient id="cancelGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ec4899" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#ec4899" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f5d0e0" />
              <XAxis dataKey="short" tick={{ fontSize: 10, fill: '#9f6478' }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10, fill: '#9f6478' }} allowDecimals={false} width={30} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #f9a8d4' }}
                formatter={(v) => [`${v} cancelled`, '']}
                labelFormatter={(l) => l}
              />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#ec4899"
                strokeWidth={2}
                dot={{ r: 2, fill: '#ec4899' }}
                activeDot={{ r: 4 }}
                fill="url(#cancelGrad)"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-pink-100 text-pink-800 text-sm font-semibold">
          <Ban className="w-3.5 h-3.5" />
          {rows.length} cancelled parcel{rows.length === 1 ? '' : 's'}
        </div>
        <button
          onClick={downloadExcel}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white transition-colors"
          data-testid="cancellations-download-excel-btn"
          title="Download this cancellations list as Excel"
        >
          <Download className="w-3.5 h-3.5" />
          Excel
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse" data-testid="cancellations-table">
          <thead>
            <tr className="bg-pink-50">
              <th className="py-2 px-3 text-xs font-semibold text-pink-900 border border-slate-200 text-left">Tracking</th>
              <th className="py-2 px-3 text-xs font-semibold text-pink-900 border border-slate-200 text-left">Invoice</th>
              <th className="py-2 px-3 text-xs font-semibold text-pink-900 border border-slate-200 text-left">Client</th>
              <th className="py-2 px-3 text-xs font-semibold text-pink-900 border border-slate-200 text-left">Customer</th>
              <th className="py-2 px-3 text-xs font-semibold text-pink-900 border border-slate-200 text-left">Country</th>
              <th className="py-2 px-3 text-xs font-semibold text-pink-900 border border-slate-200 text-left">Service</th>
              <th className="py-2 px-3 text-xs font-semibold text-pink-900 border border-slate-200 text-left">Date</th>
              <th className="py-2 px-3 text-xs font-semibold text-pink-900 border border-slate-200 text-left">Reason</th>
              <th className="py-2 px-3 text-xs font-semibold text-pink-900 border border-slate-200 text-left">Cancelled By</th>
              <th className="py-2 px-3 text-xs font-semibold text-pink-900 border border-slate-200 text-left">When</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="hover:bg-pink-50/40 transition-colors" data-testid={`cancel-row-${i}`}>
                <td className="py-1.5 px-3 text-xs font-mono text-slate-800 border border-slate-200 whitespace-nowrap">
                  {r.track_no}
                  {r.product_count > 1 && (
                    <span className="ml-1.5 text-[10px] text-slate-400" title={`${r.product_count} products in this parcel`}>
                      ({r.product_count}p)
                    </span>
                  )}
                </td>
                <td className="py-1.5 px-3 text-xs font-semibold text-slate-800 border border-slate-200">{r.invoice_no || '—'}</td>
                <td className="py-1.5 px-3 text-xs text-slate-700 border border-slate-200">{r.client_code || '—'}</td>
                <td className="py-1.5 px-3 text-xs text-slate-700 border border-slate-200">{r.customer_name || '—'}</td>
                <td className="py-1.5 px-3 text-xs text-slate-700 border border-slate-200">{r.country || '—'}</td>
                <td className="py-1.5 px-3 text-xs text-slate-700 border border-slate-200">{r.service || '—'}</td>
                <td className="py-1.5 px-3 text-xs text-slate-600 border border-slate-200 whitespace-nowrap">{r.parcel_date || '—'}</td>
                <td className="py-1.5 px-3 text-xs text-slate-700 border border-slate-200 max-w-[260px]">
                  {r.cancel_note
                    ? <span title={r.cancel_note}>{r.cancel_note}</span>
                    : <span className="text-slate-400 italic">—</span>}
                </td>
                <td className="py-1.5 px-3 text-xs text-slate-700 border border-slate-200">{r.cancelled_by || <span className="text-slate-400">unknown</span>}</td>
                <td className="py-1.5 px-3 text-xs text-slate-500 border border-slate-200 whitespace-nowrap">{fmtDate(r.cancelled_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};


const InvoiceFlowTable = ({ data, user }) => {
  const [limits, setLimits] = useTurnaroundLimits();
  const [emailStatus, setEmailStatus] = useState({});
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const editRef = useRef(null);
  const clickTimerRef = useRef(null);
  const flowSel = useDocSelection();

  // Initialize email status from server data
  useEffect(() => {
    if (!data?.invoice_flow) return;
    const map = {};
    data.invoice_flow.forEach(r => {
      if (r.email_done) map[r.invoice_no] = r.email_done;
    });
    setEmailStatus(map);
  }, [data]);

  const saveEmailStatus = useCallback(async (invoiceNo, value) => {
    try {
      await axios.put(
        `${API}/reports/invoice-flow/${encodeURIComponent(invoiceNo)}/email-status`,
        { email_done: value },
        { headers: { ...getAuthHeader(), 'Content-Type': 'application/json' } }
      );
      setEmailStatus(prev => ({ ...prev, [invoiceNo]: value }));
    } catch {
      toast.error('Failed to save email status');
    }
  }, []);

  const handleCellClick = useCallback((invoiceNo) => {
    // Single click — open text edit after short delay (to allow double-click detection)
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    clickTimerRef.current = setTimeout(() => {
      setEditingCell(invoiceNo);
      setEditValue(emailStatus[invoiceNo] || '');
    }, 250);
  }, [emailStatus]);

  const handleCellDoubleClick = useCallback((invoiceNo) => {
    // Double click — immediately set "Yes"
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    setEditingCell(null);
    saveEmailStatus(invoiceNo, 'Yes');
  }, [saveEmailStatus]);

  const handleEditSave = useCallback((invoiceNo) => {
    setEditingCell(null);
    saveEmailStatus(invoiceNo, editValue.trim());
  }, [editValue, saveEmailStatus]);

  const handleEditKeyDown = useCallback((e, invoiceNo) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleEditSave(invoiceNo);
    } else if (e.key === 'Escape') {
      setEditingCell(null);
    }
  }, [handleEditSave]);

  // Auto-focus edit input
  useEffect(() => {
    if (editingCell && editRef.current) editRef.current.focus();
  }, [editingCell]);

  if (!data || !data.invoice_flow || data.invoice_flow.length === 0) {
    return (
      <div className="p-8 text-center text-slate-400">
        <GitBranch className="w-10 h-10 mx-auto mb-2 opacity-40" />
        <p className="text-sm">No invoice flow data for this period</p>
      </div>
    );
  }

  const formatTs = (ts) => {
    if (!ts) return null;
    try {
      const d = new Date(ts);
      return d.toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
      });
    } catch { return ts; }
  };

  const stages = [
    { key: 'order_start_at', label: 'First Order' },
    { key: 'packing_list_at', label: 'Packing List', byKey: 'packing_list_by' },
    { key: 'bulk_data_at', label: 'Bulk Data', byKey: 'bulk_data_by' },
    { key: 'pack_team_at', label: 'Pack Team', byKey: 'pack_team_by' },
    { key: 'bulk_label_at', label: 'Bulk Label', byKey: 'bulk_label_by' },
  ];

  const isSlow = (h, lim) => h != null && lim && h > lim;

  return (
    <div className="p-4 pt-2 overflow-x-auto">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1"><TurnaroundSummary rows={data.invoice_flow} limits={limits} /></div>
        {user?.role === 'admin' && <div className="flex gap-2"><TurnaroundDigestButton /><TurnaroundLimitsEditor limits={limits} onSaved={setLimits} /></div>}
      </div>
      <div className="my-2 print:hidden"><DocPrintBar keys={data.invoice_flow.map(r => r.invoice_no).filter(Boolean)} sel={flowSel.sel} setSel={flowSel.setSel} testid="flow-docprint" /></div>
      <table className="w-full border-collapse" data-testid="invoice-flow-table">
        <thead>
          <tr>
            <th className="bg-[#2F5496] text-white text-xs font-semibold py-2 px-2 border border-slate-300 w-8 print:hidden"></th>
            <th className="bg-[#2F5496] text-white text-xs font-semibold py-2 px-3 text-left border border-slate-300 whitespace-nowrap">Invoice No</th>
            {stages.map(s => (
              <th key={s.key} className="bg-[#2F5496] text-white text-xs font-semibold py-2 px-2 text-center border border-slate-300 whitespace-nowrap">
                {s.label}
              </th>
            ))}
            <th className="bg-[#2F5496] text-white text-xs font-semibold py-2 px-2 text-center border border-slate-300 whitespace-nowrap">Total Time</th>
            <th className="bg-[#2F5496] text-white text-xs font-semibold py-2 px-2 text-center border border-slate-300 whitespace-nowrap">
              <div className="flex items-center justify-center gap-1"><Mail className="w-3 h-3" /> Email Done</div>
            </th>
          </tr>
        </thead>
        <tbody>
          {data.invoice_flow.map((row, i) => {
            const ta = rowTurnaround(row);
            const totalDuration = fmtHours(ta.total);
            const totalSlow = isSlow(ta.total, Number(limits?.total));
            const completedStages = stages.filter(s => row[s.key]).length;

            return (
              <tr key={i} className={`hover:bg-slate-50 transition-colors ${flowSel.sel.has(row.invoice_no) ? 'bg-indigo-50/60' : ''}`}>
                <td className="py-1.5 px-2 text-center border border-slate-200 print:hidden">
                  {row.invoice_no && <SelectBox k={row.invoice_no} sel={flowSel.sel} toggle={flowSel.toggle} testid={`flow-select-${row.invoice_no}`} />}
                </td>
                <td className="py-1.5 px-3 text-sm font-semibold text-slate-800 border border-slate-200 whitespace-nowrap">
                  {row.invoice_no}
                </td>
                {stages.map(s => {
                  const ts = row[s.key];
                  const byName = row[s.byKey] || '';
                  const stepH = ta[s.key];
                  const lim = stepLimitHours(ta, s.key, limits);
                  const slow = isSlow(stepH, lim);
                  return (
                    <td key={s.key} className={`py-1.5 px-2 text-center border border-slate-200 ${slow ? 'bg-rose-50' : ts ? 'bg-emerald-50' : ''}`}
                        data-testid={`flow-cell-${row.invoice_no}-${s.key}`}>
                      {ts ? (
                        <div>
                          <div className="text-xs font-medium text-slate-800">{formatTs(ts)}</div>
                          {byName && <div className="text-[10px] text-slate-400 mt-0.5">{byName}</div>}
                          {stepH != null && stepH >= 0 && (
                            <div className={`text-[10px] mt-0.5 font-semibold ${slow ? 'text-rose-600' : 'text-slate-500'}`}
                                 data-testid={`flow-step-${row.invoice_no}-${s.key}`} title={slow ? `Slower than the ${lim}h limit` : 'Time from previous step'}>
                              +{fmtHours(stepH)}{slow ? ' · slow' : ''}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>
                  );
                })}
                <td className="py-1.5 px-2 text-center border border-slate-200 whitespace-nowrap">
                  {totalDuration ? (
                    <div className="flex items-center justify-center gap-1">
                      <Clock className={`w-3 h-3 ${totalSlow ? 'text-rose-500' : 'text-blue-500'}`} />
                      <span className={`text-xs font-semibold ${totalSlow ? 'text-rose-600' : 'text-blue-700'}`} data-testid={`flow-total-${row.invoice_no}`}>{totalDuration}{!ta.complete && ' …'}</span>
                    </div>
                  ) : (
                    <span className="text-[10px] text-slate-400">{completedStages}/5</span>
                  )}
                </td>
                {/* Email Done cell */}
                <td
                  className="py-1 px-2 text-center border border-slate-200 min-w-[120px] cursor-pointer select-none"
                  onClick={() => handleCellClick(row.invoice_no)}
                  onDoubleClick={() => handleCellDoubleClick(row.invoice_no)}
                  data-testid={`email-done-${row.invoice_no}`}
                >
                  {editingCell === row.invoice_no ? (
                    <input
                      ref={editRef}
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={() => handleEditSave(row.invoice_no)}
                      onKeyDown={(e) => handleEditKeyDown(e, row.invoice_no)}
                      className="w-full px-1.5 py-0.5 text-xs border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                      placeholder="Type reason..."
                      data-testid={`email-edit-${row.invoice_no}`}
                    />
                  ) : (
                    <EmailDoneDisplay value={emailStatus[row.invoice_no] || ''} by={row.email_done_by} />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="mt-2 flex items-center justify-between text-xs text-slate-400">
        <span>Each step shows time since the previous step (red = slower than limit) · Total = first order → labels (… = still in progress) · Double-click Email Done = Yes</span>
        <span>{data.total_invoices} invoice{data.total_invoices !== 1 ? 's' : ''} found</span>
      </div>
    </div>
  );
};

const EmailDoneDisplay = ({ value, by }) => {
  if (!value) return <span className="text-slate-300 text-xs">—</span>;
  const isYes = value.toLowerCase() === 'yes';
  return (
    <div>
      <span className={`text-xs font-semibold ${isYes ? 'text-green-600' : 'text-amber-600'}`}>
        {value}
      </span>
      {by && <div className="text-[10px] text-slate-400 mt-0.5">{by}</div>}
    </div>
  );
};

export default ReportsPanel;
